import React, { useState, useEffect, useContext } from 'react';
import { api } from './api/api';
import { AuthContext } from '../App';

const TEAM_ROLES = [
    { value: '',            label: 'All teams'   },
    { value: 'FORCE_ONE',   label: 'Force One'   },
    { value: 'BRUTE_FORCE', label: 'Brute Force' },
    { value: 'MIDLAND',     label: 'Midland'     },
    { value: 'SUPERNOVA',   label: 'Supernova'   },
    { value: 'RUSSIA',      label: 'Russia'      },
    { value: 'FRIEND',      label: 'Friend'      },
    { value: 'GUEST',       label: 'Guest'       },
    { value: 'OTHER',       label: 'Other'       },
];

// ── Constants ──────────────────────────────────────────────────────────────

const GROUPS = ['Elite', 'Master - 1', 'Master - 2', 'Master - 3', 'Master - 4', 'Master - 5'];

const SPONSOR_ATTRS = [
    { key: 'finances',     label: 'Fin.'  },
    { key: 'expectations', label: 'Exp.'  },
    { key: 'patience',     label: 'Pat.'  },
    { key: 'reputation',   label: 'Rep.'  },
    { key: 'image',        label: 'Img.'  },
    { key: 'negotiation',  label: 'Neg.'  },
];

// ── Helpers ────────────────────────────────────────────────────────────────

const formatAmount = (v) => {
    if (v == null || v === '-1') return '—';
    const n = parseInt(v, 10);
    if (isNaN(n) || n < 0) return '—';
    return n.toLocaleString();
};

const TypeBadge = ({ type }) => {
    const isSigned = type === 0;
    return (
        <span style={{
            display: 'inline-block',
            fontSize: 10, fontWeight: 700,
            background: isSigned ? '#dcfce7' : '#eff6ff',
            color: isSigned ? '#16a34a' : '#1d4ed8',
            border: `1px solid ${isSigned ? '#bbf7d0' : '#bfdbfe'}`,
            borderRadius: 4, padding: '2px 6px',
            whiteSpace: 'nowrap',
        }}>
            {isSigned ? 'Signed' : 'Neg.'}
        </span>
    );
};

const StatusBadge = ({ isTaken }) => (
    <span style={{
        display: 'inline-block',
        fontSize: 10, fontWeight: 700,
        background: isTaken ? '#fef2f2' : '#f0fdf4',
        color: isTaken ? '#dc2626' : '#16a34a',
        border: `1px solid ${isTaken ? '#fecaca' : '#bbf7d0'}`,
        borderRadius: 4, padding: '2px 7px',
        whiteSpace: 'nowrap',
    }}>
        {isTaken ? 'Taken' : 'Free'}
    </span>
);

const AttrDot = ({ value }) => {
    // value is 0-6 → display as 1-7
    const display = value != null ? value + 1 : '—';
    const color =
        value >= 5 ? '#dc2626' :
        value >= 3 ? '#d97706' :
        '#16a34a';
    return (
        <span style={{ fontWeight: 600, color, fontSize: 13 }}>{display}</span>
    );
};

// ── Team tab — sponsor history ─────────────────────────────────────────────

const TeamHistory = () => {
    const { userRole } = useContext(AuthContext);
    const isAdmin = userRole === 'ADMIN';

    const [managers,        setManagers]        = useState([]);
    const [selectedManager, setSelectedManager] = useState(null);
    const [history,         setHistory]         = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [detailLoading,   setDetailLoading]   = useState(false);
    const [error,           setError]           = useState(null);
    const [teamRole,        setTeamRole]        = useState('');

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            setSelectedManager(null);
            setHistory([]);
            try {
                const url = teamRole ? `/sponsors/managers?teamRole=${teamRole}` : '/sponsors/managers';
                const res = await api.get(url);
                setManagers(res.data);
            } catch (err) {
                console.error(err);
                setError('Failed to load managers.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [teamRole]);

    const selectManager = async (manager) => {
        setSelectedManager(manager);
        setDetailLoading(true);
        try {
            const res = await api.get(`/sponsors/history/${manager.userId}`);
            setHistory(res.data);
        } catch (err) {
            console.error(err);
            setHistory([]);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleMobileSelect = (e) => {
        const m = managers.find(m => String(m.userId) === e.target.value);
        if (m) selectManager(m);
    };

    // Group by race for display
    const byRace = history.reduce((acc, item) => {
        const key = `${item.season}_${item.raceNumber}`;
        if (!acc[key]) acc[key] = { season: item.season, raceNumber: item.raceNumber, items: [] };
        acc[key].items.push(item);
        return acc;
    }, {});
    const races = Object.values(byRace); // already sorted new-first from API

    const S = {
        sidebar: {
            width: 220,
            borderRight: '1px solid var(--c-border)',
            overflowY: 'auto',
            flexShrink: 0,
            background: '#fff',
        },
        sidebarHeader: {
            padding: '12px 16px',
            fontWeight: 700, fontSize: 11,
            borderBottom: '1px solid var(--c-border)',
            background: '#fafafa',
            textTransform: 'uppercase', letterSpacing: '0.6px',
            color: 'var(--c-text-muted)',
        },
        managerItem: (selected) => ({
            padding: '10px 16px',
            cursor: 'pointer',
            background: selected ? '#fff3f3' : 'transparent',
            borderLeft: `3px solid ${selected ? 'var(--c-accent)' : 'transparent'}`,
            fontSize: 13, fontWeight: selected ? 600 : 400,
            color: 'var(--c-text)',
            transition: 'background 0.12s',
        }),
        main: {
            flex: 1, overflowY: 'auto', padding: 24,
            background: 'var(--c-bg)', minWidth: 0,
        },
        placeholder: {
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            height: '100%', color: 'var(--c-text-light)', gap: 12,
        },
    };

    return (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Sidebar */}
            <div style={S.sidebar} className="ra-desktop-sidebar">
                <div style={S.sidebarHeader}>Managers</div>
                {isAdmin && (
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)' }}>
                        <select value={teamRole} onChange={e => setTeamRole(e.target.value)}
                            style={{ width: '100%', padding: '5px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--c-border)', fontSize: 12, background: '#fff', color: 'var(--c-text)', cursor: 'pointer' }}>
                            {TEAM_ROLES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                )}
                {loading && <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>Loading…</div>}
                {error   && <div style={{ padding: 16, color: 'var(--c-error)',      fontSize: 13 }}>{error}</div>}
                {!loading && !error && managers.length === 0 && (
                    <div style={{ padding: 16, color: 'var(--c-text-muted)', fontSize: 13 }}>No data.</div>
                )}
                {managers.map(m => (
                    <div
                        key={m.userId}
                        style={S.managerItem(selectedManager?.userId === m.userId)}
                        onClick={() => selectManager(m)}
                    >
                        {m.managerName}
                    </div>
                ))}
            </div>

            {/* Main */}
            <div style={S.main} className="ra-main">
                {/* Mobile selector */}
                <div className="ra-mobile-selector">
                    <select
                        className="fb-select"
                        style={{ width: '100%' }}
                        value={selectedManager?.userId ?? ''}
                        onChange={handleMobileSelect}
                        disabled={loading}
                    >
                        <option value="">{loading ? 'Loading…' : '— Select a manager —'}</option>
                        {managers.map(m => (
                            <option key={m.userId} value={m.userId}>{m.managerName}</option>
                        ))}
                    </select>
                </div>

                {!selectedManager && (
                    <div style={S.placeholder} className="ra-desktop-placeholder">
                        <span style={{ fontSize: 44 }}>💰</span>
                        <span style={{ fontSize: 14 }}>Select a manager to view sponsor history</span>
                    </div>
                )}

                {selectedManager && detailLoading && (
                    <div style={{ color: 'var(--c-text-muted)', marginTop: 48, textAlign: 'center', fontSize: 14 }}>
                        Loading sponsors…
                    </div>
                )}

                {selectedManager && !detailLoading && (
                    <>
                        <div className="fb-page-header">
                            <div className="fb-page-title">{selectedManager.managerName}</div>
                            <div className="fb-page-sub">
                                Sponsor history — {history.length} record{history.length !== 1 ? 's' : ''}
                            </div>
                        </div>

                        {races.length === 0 ? (
                            <div style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>No data available.</div>
                        ) : (
                            races.map(race => {
                                const signed = race.items.filter(i => i.entryType === 0);
                                const negs   = race.items.filter(i => i.entryType === 1);
                                return (
                                    <div key={`${race.season}_${race.raceNumber}`} className="fb-card">
                                        <div className="fb-card-header">
                                            <span className="fb-card-title">
                                                S{race.season} R{race.raceNumber}
                                            </span>
                                        </div>
                                        <div className="fb-card-body" style={{ padding: 0 }}>
                                            {/* Signed sponsors */}
                                            {signed.length > 0 && (
                                                <>
                                                    <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--c-text-muted)', borderBottom: '1px solid var(--c-border-light)' }}>
                                                        Signed ({signed.length})
                                                    </div>
                                                    <div className="fb-table-wrap">
                                                        <table className="fb-table fb-table-sm" style={{ whiteSpace: 'nowrap' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Sponsor</th>
                                                                    <th>Spot</th>
                                                                    <th>Amount / Race</th>
                                                                    <th>Races Left</th>
                                                                    <th>Satisfaction</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {signed.map(s => (
                                                                    <tr key={s.id}>
                                                                        <td style={{ fontWeight: 600 }}>{s.sponsorName}</td>
                                                                        <td style={{ color: 'var(--c-text-muted)' }}>{s.carSpotName || '—'}</td>
                                                                        <td>{formatAmount(s.amount)}</td>
                                                                        <td>{s.racesLeft ?? '—'}</td>
                                                                        <td>{s.satisfaction ?? '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}

                                            {/* Negotiations */}
                                            {negs.length > 0 && (
                                                <>
                                                    <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--c-text-muted)', borderBottom: '1px solid var(--c-border-light)', borderTop: signed.length > 0 ? '1px solid var(--c-border)' : 'none' }}>
                                                        Negotiations ({negs.length})
                                                    </div>
                                                    <div className="fb-table-wrap">
                                                        <table className="fb-table fb-table-sm" style={{ whiteSpace: 'nowrap' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th>Sponsor</th>
                                                                    <th>Spot</th>
                                                                    <th>Progress</th>
                                                                    <th>Priority</th>
                                                                    <th>Contested</th>
                                                                    <th>Amount offered</th>
                                                                    <th>Duration offered</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {negs.map(s => (
                                                                    <tr key={s.id}>
                                                                        <td style={{ fontWeight: 600 }}>{s.sponsorName}</td>
                                                                        <td style={{ color: 'var(--c-text-muted)' }}>
                                                                            {s.carSpotName && s.carSpotName !== '-' ? s.carSpotName : '—'}
                                                                        </td>
                                                                        <td>
                                                                            {s.negProgress != null ? (
                                                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                                    <div style={{ width: 60, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                                                                        <div style={{ width: `${s.negProgress}%`, height: '100%', background: '#1d4ed8', borderRadius: 3 }} />
                                                                                    </div>
                                                                                    <span style={{ fontSize: 11 }}>{s.negProgress}%</span>
                                                                                </span>
                                                                            ) : '—'}
                                                                        </td>
                                                                        <td>{s.negPriority ?? '—'}</td>
                                                                        <td>
                                                                            {s.negContested != null ? (
                                                                                <span style={{ color: s.negContested === 'Yes' ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                                                                                    {s.negContested}
                                                                                </span>
                                                                            ) : '—'}
                                                                        </td>
                                                                        <td>{formatAmount(s.amount)}</td>
                                                                        <td>{s.negDuration ?? '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// ── Groups tab ─────────────────────────────────────────────────────────────

const SponsorGroups = () => {
    const [groups,  setGroups]  = useState({});
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get('/sponsors/groups/all');
                setGroups(res.data);
            } catch (err) {
                console.error(err);
                setError('Failed to load sponsor groups.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading) {
        return <div style={{ padding: 32, color: 'var(--c-text-muted)', textAlign: 'center', fontSize: 14 }}>Loading groups…</div>;
    }
    if (error) {
        return <div style={{ padding: 32, color: 'var(--c-error)', textAlign: 'center', fontSize: 14 }}>{error}</div>;
    }

    // Row background based on state
    const rowBg = (s) => {
        if (!s.isTaken) return '#f0fdf4';                         // free — green tint
        if (s.duration != null && s.duration <= 3) return '#fef2f2'; // expiring soon — red tint
        return undefined;
    };

    // updatedAt from first sponsor in group (all same timestamp)
    const groupUpdated = (sponsors) => {
        const ts = sponsors[0]?.updatedAt;
        if (!ts) return null;
        return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }} className="ra-main">
            {GROUPS.map(groupName => {
                // Server already sorted: free first, then taken by duration asc, then taken+null
                const sponsors = groups[groupName] ?? [];
                if (sponsors.length === 0) return null;

                const takenCount = sponsors.filter(s => s.isTaken).length;
                const freeCount  = sponsors.length - takenCount;
                const updated    = groupUpdated(sponsors);

                return (
                    <div key={groupName} className="fb-card">
                        <div className="fb-card-header">
                            <span className="fb-card-title">{groupName}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {updated && (
                                    <span style={{ fontSize: 11, color: 'var(--c-text-light)' }}>
                                        Updated {updated}
                                    </span>
                                )}
                                <span style={{ fontSize: 12 }}>
                                    <span style={{ color: '#16a34a', fontWeight: 600 }}>{freeCount} free</span>
                                    {' · '}
                                    <span style={{ color: '#dc2626', fontWeight: 600 }}>{takenCount} taken</span>
                                </span>
                            </span>
                        </div>
                        <div className="sg-table-wrap">
                            <table className="fb-table fb-table-sm sg-table" style={{ whiteSpace: 'nowrap' }}>
                                <thead>
                                    <tr>
                                        <th>Sponsor</th>
                                        <th>Group</th>
                                        {SPONSOR_ATTRS.map(a => (
                                            <th key={a.key} style={{ textAlign: 'center' }}>{a.label}</th>
                                        ))}
                                        <th>Status</th>
                                        <th>Avg Neg</th>
                                        <th>Detail Avg</th>
                                        <th>Manager</th>
                                        <th>Races left</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sponsors.map(s => (
                                        <tr key={s.sponsorId} style={{ background: rowBg(s) }}>
                                            <td style={{ fontWeight: 600 }}>{s.sponsorName}</td>
                                            <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                                                {s.sponsorGroup ?? '—'}
                                            </td>
                                            {SPONSOR_ATTRS.map(a => (
                                                <td key={a.key} style={{ textAlign: 'center' }}>
                                                    <AttrDot value={s[a.key]} />
                                                </td>
                                            ))}
                                            <td><StatusBadge isTaken={s.isTaken} /></td>
                                            <td>
                                                {!s.isTaken && s.estAvgProgress != null ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                        <div style={{ width: 52, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                                            <div style={{ width: `${s.estAvgProgress}%`, height: '100%', background: '#1d4ed8', borderRadius: 3 }} />
                                                        </div>
                                                        <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{s.estAvgProgress}%</span>
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td>
                                                {!s.isTaken && s.avgProgress != null ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                        <div style={{ width: 52, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                                            <div style={{ width: `${Math.min(s.avgProgress, 100)}%`, height: '100%', background: '#7c3aed', borderRadius: 3 }} />
                                                        </div>
                                                        <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{s.avgProgress.toFixed(1)}%</span>
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td style={{ color: 'var(--c-text-muted)', fontSize: 12 }}>
                                                {!s.isTaken
                                                    ? '—'
                                                    : (s.managerName ?? <span style={{ color: '#d97706', fontWeight: 600 }}>?</span>)
                                                }
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {!s.isTaken ? '—' : s.duration != null
                                                    ? <span style={{ fontWeight: 700, color: s.duration <= 3 ? '#dc2626' : 'inherit' }}>{s.duration}</span>
                                                    : <span style={{ color: '#d97706', fontWeight: 600 }}>?</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ── Main Sponsors page ─────────────────────────────────────────────────────

const Sponsors = () => {
    const [tab, setTab] = useState('team');

    const tabStyle = (active) => ({
        padding: '9px 20px',
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        fontWeight: active ? 700 : 400,
        fontSize: 13,
        color: active ? 'var(--c-accent)' : 'var(--c-text-muted)',
        borderBottom: `2px solid ${active ? 'var(--c-accent)' : 'transparent'}`,
        marginBottom: -2,
        transition: 'color 0.15s',
    });

    return (
        <div className="ra-page" style={{ flexDirection: 'column' }}>
            {/* Tab bar */}
            <div style={{
                display: 'flex',
                borderBottom: '2px solid var(--c-border)',
                background: '#fff',
                paddingLeft: 24,
                flexShrink: 0,
            }}>
                <button style={tabStyle(tab === 'team')}   onClick={() => setTab('team')}>Team History</button>
                <button style={tabStyle(tab === 'groups')} onClick={() => setTab('groups')}>Sponsor Groups</button>
            </div>

            {/* Tab content */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {tab === 'team'   && <TeamHistory />}
                {tab === 'groups' && <SponsorGroups />}
            </div>
        </div>
    );
};

export default Sponsors;
